import { useLazyQuery } from "@apollo/client";
import { Box, CircularProgress, Typography } from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";
import { fetchPrelemContent } from "@platformx/authoring-apis";
import React, { useEffect, useState } from "react";
import PrelemsListContainer from "./PrelemsListContainer";

const useStyles = makeStyles(() =>
  createStyles({
    listBox: {
      marginBottom: "24px",
      borderRadius: "8px",
      overflow: "hidden",
    },
    itemLoader: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: "40px 0",
    },
    itemError: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: "20px 0",
      color: "#d32f2f",
    },
  }),
);

export interface PrelemItem {
  PrelemId: string;
  PrelemName: string;
  Tags: string[];
  PreviewThumbnail: string;
  Thumbnails: string[];
  Description: string;
  DocumentType: string;
  DocumentPath: string;
  DevelopedBy: string;
  DevelopedDate: string;
  DocumentCreationPath: string;
  SeoEnabled: boolean;
  AnalyticsEnabled: boolean;
}

interface PrelemListItemProps {
  prelem: PrelemItem;
  refreshKey: number;
}

/**
 * Fetches and renders a single prelem's content independently.
 * Each item manages its own loading/error state so one failure doesn't affect others.
 */
const PrelemListItem = React.memo(function PrelemListItem({
  prelem,
  refreshKey,
}: PrelemListItemProps) {
  const classes = useStyles();
  const [content, setContent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [runFetchContentQuery] = useLazyQuery(fetchPrelemContent);

  useEffect(() => {
    let cancelled = false;

    const fetchContent = async () => {
      setLoading(true);
      setError(false);
      try {
        const contentRes = await runFetchContentQuery({
          variables: {
            path: prelem.DocumentPath,
            docType: prelem.DocumentType,
            prelemId: prelem.PrelemId,
          },
        });
        if (!cancelled) {
          const data = contentRes?.data?.authoring_getCmsItemContent;
          if (data) {
            setContent(data);
          } else {
            setError(true);
          }
        }
      } catch (e) {
        console.error(`Error fetching content for ${prelem.PrelemId}`, e);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchContent();
    return () => {
      cancelled = true;
    };
  }, [refreshKey, prelem.DocumentPath, prelem.DocumentType, prelem.PrelemId, runFetchContentQuery]);

  return (
    <div>
      <h2>
        {prelem.PrelemName} {prelem.PrelemId}
      </h2>
      <Box className={classes.listBox}>
        {loading && (
          <Box className={classes.itemLoader}>
            <CircularProgress size={28} />
          </Box>
        )}
        {error && !loading && (
          <Box className={classes.itemError}>
            <Typography variant='body1'>Failed to load prelem content</Typography>
          </Box>
        )}
        {!loading && !error && content && (
          <PrelemsListContainer prelemContent={content} PrelemId={prelem.PrelemId} />
        )}
      </Box>
    </div>
  );
});

export default PrelemListItem;
